import { useEffect, useState } from 'react';
import { api } from '../api.js';
import { tokens, couleursStatut, couleursGravite, couleursImpact, couleursUrgence, couleursType, STATUTS_CLOS } from '../tokens.js';
import { Badge, boutonPrincipal, formaterDateRelative } from '../helpers.jsx';
import { Plus, Inbox, AlertCircle, ChevronDown, ChevronRight, Archive } from 'lucide-react';

// Liste des tickets du declarant courant (cross-app).
export function Dashboard({ utilisateur, onNouveau, onOuvrirTicket }) {
  const [tickets, setTickets] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [closOuvert, setClosOuvert] = useState(false);

  useEffect(() => {
    setChargement(true);
    api.get('/mes-tickets')
      .then(d => setTickets(d.tickets || []))
      .catch(e => setErreur(e.message || 'Erreur de chargement'))
      .finally(() => setChargement(false));
  }, []);

  const actifs = tickets.filter(t => !STATUTS_CLOS.includes(t.statut));
  const clos   = tickets.filter(t =>  STATUTS_CLOS.includes(t.statut));

  return (
    <div>
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        marginBottom: 22,
      }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: tokens.ink }}>
            Mes tickets
          </h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.inkSoft }}>
            Bonjour {utilisateur.nom_complet.split(' ')[0]}, voici tes signalements.
          </p>
        </div>
        <button onClick={onNouveau} style={boutonPrincipal}>
          <Plus size={16} /> Nouveau ticket
        </button>
      </div>

      {chargement && <div style={etatVide}>Chargement…</div>}

      {erreur && !chargement && (
        <div style={{ ...etatVide, color: tokens.danger }}>
          <AlertCircle size={20} /> {erreur}
        </div>
      )}

      {!chargement && !erreur && tickets.length === 0 && (
        <div style={etatVide}>
          <Inbox size={32} color={tokens.inkMuted} />
          <div>Aucun ticket pour le moment.</div>
          <button onClick={onNouveau} style={{ ...boutonPrincipal, marginTop: 8 }}>
            <Plus size={16} /> Créer mon premier ticket
          </button>
        </div>
      )}

      {!chargement && tickets.length > 0 && (
        <>
          {/* Actifs */}
          <div style={{ marginBottom: 18 }}>
            <h3 style={titreSection}>
              En cours ({actifs.length})
            </h3>
            {actifs.length === 0 ? (
              <div style={{
                ...etatVide, padding: 24, fontSize: 13,
                color: tokens.inkMuted,
              }}>
                Aucun ticket actif. 🎉
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap: 10 }}>
                {actifs.map(t => (
                  <CarteTicket key={t.id} ticket={t} onClic={() => onOuvrirTicket(t.token_public)} />
                ))}
              </div>
            )}
          </div>

          {/* Clos collapsable */}
          {clos.length > 0 && (
            <div>
              <button
                onClick={() => setClosOuvert(o => !o)}
                style={{
                  display:'flex', alignItems:'center', gap: 8,
                  background:'transparent', border:'none', cursor:'pointer',
                  fontSize: 13, fontWeight: 600, color: tokens.inkSoft,
                  textTransform:'uppercase', letterSpacing: 0.5,
                  padding: '8px 0',
                }}
              >
                {closOuvert ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                <Archive size={13} /> Clos & refusés ({clos.length})
              </button>
              {closOuvert && (
                <div style={{ display:'flex', flexDirection:'column', gap: 10, opacity: 0.85 }}>
                  {clos.map(t => (
                    <CarteTicket key={t.id} ticket={t} onClic={() => onOuvrirTicket(t.token_public)} />
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const titreSection = {
  margin: '0 0 10px',
  fontSize: 13,
  fontWeight: 600,
  color: tokens.inkSoft,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
};

function CarteTicket({ ticket, onClic }) {
  const statut  = couleursStatut[ticket.statut] || couleursStatut.nouveau;
  const gravite = ticket.gravite_ia ? couleursGravite[ticket.gravite_ia] : null;
  const impact  = couleursImpact[ticket.impact];
  const urgence = couleursUrgence[ticket.urgence];

  const cheminService = ticket.parent_service_nom
    ? `${ticket.parent_service_nom} / ${ticket.service_nom}`
    : ticket.service_nom;

  return (
    <div
      onClick={onClic}
      style={{
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: 10,
        padding: '14px 18px',
        cursor: 'pointer',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = tokens.accentSoft;
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(107,63,160,0.08)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = tokens.border;
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap', marginBottom: 6 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 12, color: tokens.inkMuted }}>
          #{ticket.token_public}
        </span>
        {ticket.type && couleursType[ticket.type] && (
          <Badge bg={couleursType[ticket.type].bg} fg={couleursType[ticket.type].fg}>
            {couleursType[ticket.type].label}
          </Badge>
        )}
        <Badge bg={statut.bg}  fg={statut.fg}>{statut.label}</Badge>
        {impact && <Badge bg={impact.bg} fg={impact.fg}>{impact.label}</Badge>}
        {ticket.urgence === 'urgent' && (
          <Badge bg={urgence.bg} fg={urgence.fg}>{urgence.label}</Badge>
        )}
        {gravite && <Badge bg={gravite.bg} fg={gravite.fg} taille="petit">IA: {gravite.label}</Badge>}
        <span style={{ fontSize: 11, color: tokens.inkMuted }}>{cheminService}</span>
        <span style={{ marginLeft: 'auto', fontSize: 11, color: tokens.inkMuted }}>
          {formaterDateRelative(ticket.cree_le)}
        </span>
      </div>
      <div style={{
        fontSize: 14, color: tokens.ink, lineHeight: 1.4,
        overflow: 'hidden', textOverflow: 'ellipsis',
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
      }}>
        {ticket.resume_ia || ticket.description_action}
      </div>
    </div>
  );
}

const etatVide = {
  background: tokens.surface,
  border: `1px solid ${tokens.border}`,
  borderRadius: 10,
  padding: 48,
  textAlign: 'center',
  color: tokens.inkSoft,
  fontSize: 14,
  display:'flex', flexDirection:'column', alignItems:'center', gap: 12,
};
